import React, { useState, useEffect, useCallback } from 'react';
import { homework } from '../../api';
import ProgressBar from './ProgressBar';
import '../Tasks/Tasks.css';
import './Homework.css';

const REFRESH_MS = 30 * 1000;
const MAX_TASKS = 30;

const dueText = (d) => new Date(d).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
const isLate = (hw) => hw.dueDate && !hw.completedAt && new Date(hw.dueDate) < new Date();
const errorOf = (err, fallback) => err?.response?.data?.error || fallback;

// Coach and head coach: give homework to players and watch how far each player is, as a percentage bar.
const CoachHomework = ({ isHeadcoach }) => {
  const [players, setPlayers] = useState(null);
  const [team, setTeam] = useState(null);
  const [openId, setOpenId] = useState('');
  const [details, setDetails] = useState({});   // playerId -> { homework: [...] }
  const [form, setForm] = useState(null);       // null (closed) or { playerIds: [...] }
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [tasks, setTasks] = useState(['']);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadDetail = useCallback((id) => {
    return homework.getFor(id)
      .then(res => setDetails(prev => ({ ...prev, [id]: res.data })))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    homework.getPlayers()
      .then(res => setPlayers(res.data.players || []))
      .catch(() => setPlayers(prev => prev || []));
    if (isHeadcoach) {
      homework.getTeam().then(res => setTeam(res.data)).catch(() => {});
    }
  }, [isHeadcoach]);

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (document.hidden) return;
      load();
      if (openId) loadDetail(openId);
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load, loadDetail, openId]);

  // A "finished their homework" notification lands here: refresh straight away
  useEffect(() => {
    const onTap = () => { load(); if (openId) loadDetail(openId); };
    window.addEventListener('notificationNavTarget', onTap);
    return () => window.removeEventListener('notificationNavTarget', onTap);
  }, [load, loadDetail, openId]);

  const toggleOpen = (id) => {
    if (openId === id) { setOpenId(''); return; }
    setOpenId(id);
    loadDetail(id);
  };

  const openForm = (playerIds = []) => {
    setForm({ playerIds });
    setTitle(''); setNote(''); setDueDate(''); setTasks(['']); setError(''); setNotice('');
  };

  const togglePlayer = (id) => setForm(f => ({ ...f, playerIds: f.playerIds.includes(id) ? f.playerIds.filter(x => x !== id) : [...f.playerIds, id] }));
  const setTask = (i, text) => setTasks(prev => prev.map((t, idx) => (idx === i ? text : t)));
  const addTask = () => setTasks(prev => (prev.length >= MAX_TASKS ? prev : [...prev, '']));
  const removeTask = (i) => setTasks(prev => (prev.length === 1 ? [''] : prev.filter((_, idx) => idx !== i)));

  const send = async () => {
    const clean = tasks.map(t => t.trim()).filter(Boolean);
    if (form.playerIds.length === 0) { setError('Choose at least one player'); return; }
    if (clean.length === 0) { setError('Add at least one task'); return; }
    setSending(true);
    setError('');
    try {
      const res = await homework.give({ playerIds: form.playerIds, title: title.trim(), note: note.trim(), dueDate: dueDate || undefined, tasks: clean });
      const n = (res.data.homework || []).length;
      setNotice(`Homework sent to ${n} ${n === 1 ? 'player' : 'players'}`);
      setForm(null);
      load();
      if (openId) loadDetail(openId);
      setTimeout(() => setNotice(''), 4000);
    } catch (err) {
      setError(errorOf(err, 'Could not send the homework. Try again.'));
    } finally {
      setSending(false);
    }
  };

  const remove = async (hw, playerId) => {
    if (!window.confirm('Take this homework back? The player will no longer see it.')) return;
    try {
      await homework.remove(hw._id);
      loadDetail(playerId);
      load();
    } catch (err) {
      setError(errorOf(err, 'Could not remove it'));
    }
  };

  return (
    <div className="hw-wrap">
      {notice && <div className="hw-notice" role="status">✅ {notice}</div>}

      <button type="button" className="hw-primary" onClick={() => openForm(openId ? [openId] : [])}>
        ➕ Give homework
      </button>

      {isHeadcoach && team && team.coaches.length > 0 && (
        <div className="hw-card">
          <div className="hw-head">
            <h3 className="hw-h">Whole team</h3>
            <span className="hw-sub">{team.done} of {team.total} tasks done</span>
          </div>
          <ProgressBar percent={team.percent} total={team.total} />
          <div style={{ marginTop: 10 }}>
            {team.coaches.map(c => (
              <div key={c._id} style={{ padding: '8px 0' }}>
                <div className="hw-player-top" style={{ marginBottom: 6 }}>
                  <span className="hw-player-name">{c.name}</span>
                  <span className="hw-sub">{c.players} {c.players === 1 ? 'player' : 'players'} · {c.open} open</span>
                </div>
                <ProgressBar percent={c.percent} total={c.total} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="hw-card">
        <div className="hw-head">
          <h3 className="hw-h">{isHeadcoach ? 'Your players' : 'My players'}</h3>
          <span className="hw-sub">Tap a player to see their homework</span>
        </div>
        {players === null && <div className="hw-sub">Loading…</div>}
        {players && players.length === 0 && <div className="hw-sub">No players yet. Add students first, then give them homework.</div>}
        {players && players.map(p => (
          <div key={p._id}>
            <button type="button" className="hw-player" onClick={() => toggleOpen(p._id)} aria-expanded={openId === p._id}>
              <div className="hw-player-top">
                <span className="hw-player-name">{p.name}</span>
                {p.assignments === 0
                  ? <span className="hw-sub">No homework yet</span>
                  : p.open > 0
                    ? <span className="hw-pill">{p.open} open</span>
                    : <span className="hw-pill hw-pill--ok">All done</span>}
              </div>
              {p.assignments > 0 && <ProgressBar percent={p.percent} total={p.total} />}
            </button>
            {openId === p._id && (
              <div className="hw-detail">
                {!details[p._id] && <div className="hw-sub">Loading…</div>}
                {details[p._id] && details[p._id].homework.length === 0 && <div className="hw-sub">Nothing given to {p.name} yet.</div>}
                {details[p._id] && details[p._id].homework.map(hw => (
                  <div key={hw._id} className="hw-assign">
                    <div className="hw-player-top">
                      <span className="hw-assign-title">{hw.title || 'Homework'}</span>
                      {hw.completedAt
                        ? <span className="hw-pill hw-pill--ok">Done</span>
                        : isLate(hw) ? <span className="hw-pill hw-pill--late">Overdue</span>
                        : hw.dueDate ? <span className="hw-pill">Due {dueText(hw.dueDate)}</span> : null}
                    </div>
                    <ProgressBar percent={hw.percent} total={hw.total} />
                    <div className="hw-sub">{hw.done} of {hw.total} done</div>
                    {hw.items.map(item => (
                      <div key={item._id} className="hw-mini-task" data-done={item.done}>
                        <span className="hw-mini-mark">{item.done ? '✅' : '⬜'}</span>
                        <span className="hw-mini-text">{item.text}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <button type="button" className="hw-ghost" onClick={() => openForm([p._id])}>Give more</button>
                      <button type="button" className="hw-ghost hw-danger" onClick={() => remove(hw, p._id)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {form && (
        <div className="tl-dialog-back" onClick={() => !sending && setForm(null)}>
          <div className="tl-dialog tl-form" role="dialog" aria-label="Give homework" onClick={(e) => e.stopPropagation()}>
            <h3 className="hw-h" style={{ marginBottom: 4 }}>Give homework</h3>

            <label className="tl-label">Who is it for?</label>
            <div className="tl-coaches">
              {(players || []).length > 1 && (
                <label className="tl-coach">
                  <input
                    type="checkbox"
                    checked={form.playerIds.length === players.length}
                    onChange={(e) => setForm(f => ({ ...f, playerIds: e.target.checked ? players.map(p => p._id) : [] }))}
                  />
                  <strong>Everyone</strong>
                </label>
              )}
              {(players || []).map(p => (
                <label key={p._id} className="tl-coach">
                  <input type="checkbox" checked={form.playerIds.includes(p._id)} onChange={() => togglePlayer(p._id)} />
                  {p.name}
                </label>
              ))}
            </div>

            <label className="tl-label">Title (optional)</label>
            <input className="tl-input" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Backhand volley week" />

            <label className="tl-label">Due date (optional)</label>
            <input className="tl-input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />

            <label className="tl-label">Tasks</label>
            {tasks.map((t, i) => (
              <div key={i} className="tl-task-row">
                <span className="tl-mark">{i + 1}.</span>
                <input className="tl-input" value={t} maxLength={300} onChange={(e) => setTask(i, e.target.value)} placeholder="e.g. 50 wall volleys, both sides" />
                <button type="button" className="tl-x" aria-label="Remove task" onClick={() => removeTask(i)}>✕</button>
              </div>
            ))}
            {tasks.length < MAX_TASKS && (
              <button type="button" className="hw-ghost" onClick={addTask}>＋ Add a task</button>
            )}

            <label className="tl-label">Note for the player (optional)</label>
            <textarea className="tl-input" rows={2} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />

            {error && <p className="tl-error">{error}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button type="button" className="hw-ghost" style={{ flex: 1 }} disabled={sending} onClick={() => setForm(null)}>Cancel</button>
              <button type="button" className="hw-primary" style={{ flex: 2 }} disabled={sending} onClick={send}>
                {sending ? 'Sending…' : 'Send homework'}
              </button>
            </div>
          </div>
        </div>
      )}
      {!form && error && <p className="tl-error" style={{ textAlign: 'center' }}>{error}</p>}
    </div>
  );
};

export default CoachHomework;
